import { useEffect, useRef, useState } from 'react';
import { AlertDialog, Button, Callout, Card, EmptyState, Spinner, Table, Tag } from '@humanforce/mango-ui-react';
import { ToastContainer, useToast } from '../../../components/ui';
import type { PayAdviceTemplateDetailsDto, PayAdviceTemplateSummaryDto } from '../organisationApiTypes';
import { advisoryTone } from '../payAdviceAdvisoryTone';
import {
  archivePayAdviceTemplate,
  createPayAdviceTemplate,
  getPayAdviceTemplate,
  listPayAdviceTemplates,
  setDefaultPayAdviceTemplate,
  updatePayAdviceTemplate,
} from '../payAdviceApi';
import { PayAdviceTemplateWizard } from './PayAdviceTemplateWizard';

interface PayAdvicePanelProps {
  businessRecordId: string;
}

const BACK_PAY_LABEL: Record<string, string> = { off: 'Off', summary: 'Summary', detailed: 'Detailed' };
const AGGREGATION_LABEL: Record<string, string> = { roll_up: 'Roll up', date_by_date: 'Date by date', date_by_date_with_summary: 'Date by date + summary' };
const FDVL_LABEL: Record<string, string> = { pay_type: 'Pay Type', free_text: 'Free text' };
const LEAVE_LABEL: Record<string, string> = { off: 'Off', eofy: 'To EOFY', anniversary: 'To anniversary' };
const DELIVERY_LABEL: Record<string, string> = { electronic: 'Electronic', print: 'Print', both: 'Both' };

export const PayAdvicePanel = ({ businessRecordId }: PayAdvicePanelProps) => {
  const [list, setList] = useState<PayAdviceTemplateSummaryDto[]>([]);
  const [meta, setMeta] = useState<{ activeCount: number; totalCount: number; businessRecordInactive: boolean; canCreate: boolean } | null>(null);
  const [advisories, setAdvisories] = useState<{ code: string; message: string; targetId?: string | null }[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refresh, setRefresh] = useState(0);

  const [wizardMode, setWizardMode] = useState<'create' | 'edit' | null>(null);
  const [wizardTemplate, setWizardTemplate] = useState<PayAdviceTemplateDetailsDto | null>(null);
  const [wizardReadOnly, setWizardReadOnly] = useState(false);
  // The FW Reg 3.46 list is identical across every template for a Business
  // Record (D-10 — never influenced by any request field), so it is sourced
  // once from whichever template's details are read first, and reused for
  // "create" too rather than left empty until an edit happens to populate it.
  const [mandatoryContent, setMandatoryContent] = useState<PayAdviceTemplateDetailsDto['mandatoryContent']>([]);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const [archiveTarget, setArchiveTarget] = useState<PayAdviceTemplateSummaryDto | null>(null);
  const [archiving, setArchiving] = useState(false);

  const { addToast, removeToast, toasts } = useToast();

  useEffect(() => {
    let mounted = true;

    const load = async () => {
      setLoading(true);
      setError(null);
      let result: Awaited<ReturnType<typeof listPayAdviceTemplates>>;
      try {
        result = await listPayAdviceTemplates(businessRecordId);
      } catch (reason) {
        if (mounted) { setError(reason instanceof Error ? reason.message : 'Failed to load Pay Advice Templates.'); setLoading(false); }
        return;
      }
      if (!mounted) return;
      setList(result.templates);
      setMeta({ activeCount: result.activeCount, totalCount: result.totalCount, businessRecordInactive: result.meta.businessRecordInactive, canCreate: result.meta.actions.canCreate });
      setAdvisories(result.advisories);
      setLoading(false);

      const seedTemplateId = result.templates[0]?.payAdviceTemplateId;
      if (!seedTemplateId) return;
      // Seeds the mandatory-content list from whichever template loads
      // first, so "New template" has it populated even before any edit. A
      // failure here is secondary to a successfully loaded list — surfaced
      // as a toast rather than replacing the list with a full-page error
      // (leos-hf, PR #194 review).
      try {
        const details = await getPayAdviceTemplate(businessRecordId, seedTemplateId);
        if (mounted) setMandatoryContent(details.mandatoryContent);
      } catch (reason) {
        if (mounted) addToast(reason instanceof Error ? reason.message : 'Failed to load mandatory content details.', 'caution');
      }
    };

    void load();
    return () => { mounted = false; };
  }, [businessRecordId, refresh, addToast]);

  const openCreate = () => {
    openEditToken.current += 1; // supersedes any in-flight openEdit request
    setWizardTemplate(null);
    setWizardReadOnly(false);
    setSaveError(null);
    setWizardMode('create');
  };

  // Guards against two quick row clicks resolving out of order: if a newer
  // openEdit call has started by the time this one's response arrives, the
  // stale response is discarded rather than overwriting the newer selection
  // (tech-lead-review F-3).
  const openEditToken = useRef(0);

  const openEdit = async (templateId: string) => {
    setSaveError(null);
    const token = (openEditToken.current += 1);
    try {
      const details = await getPayAdviceTemplate(businessRecordId, templateId);
      if (token !== openEditToken.current) return;
      setWizardTemplate(details);
      setMandatoryContent(details.mandatoryContent);
      // The server's own capability flag decides editability (never re-derived
      // client-side) — an archived template or one under an inactive Business
      // Record remains openable to view, per plan section 10.2, but not to edit.
      setWizardReadOnly(!details.meta.actions.canEdit);
      setWizardMode('edit');
    } catch (reason) {
      if (token !== openEditToken.current) return;
      // The list itself is already loaded and showing — a failed row-detail
      // fetch is surfaced as a toast rather than replacing the list with a
      // full-page error, which would hide every other row's actions too
      // (leos-hf, PR #194 review).
      addToast(reason instanceof Error ? reason.message : 'Failed to load the template.', 'critical');
    }
  };

  const save = async (request: Parameters<typeof createPayAdviceTemplate>[1]) => {
    setSaving(true);
    setSaveError(null);
    try {
      const result = wizardMode === 'edit' && wizardTemplate
        ? await updatePayAdviceTemplate(businessRecordId, wizardTemplate.payAdviceTemplateId, request)
        : await createPayAdviceTemplate(businessRecordId, request);
      setWizardMode(null);
      setRefresh((value) => value + 1);
      addToast(wizardMode === 'edit' ? 'Template updated.' : 'Template created.', 'positive');
      // Advisories on the save response (e.g. an unverified FDVL Pay Type, or
      // delivery readiness) are non-blocking by design — surface them rather
      // than discard the response that carries them.
      result.advisories.forEach((advisory) => addToast(advisory.message, 'caution'));
    } catch (reason) {
      // The wizard stays open on failure so the message is rendered inside it —
      // at panel level it would sit behind the modal overlay and the operator
      // would see nothing happen.
      setSaveError(reason instanceof Error ? reason.message : 'Failed to save the template.');
    } finally {
      setSaving(false);
    }
  };

  const confirmArchive = async () => {
    if (!archiveTarget) return;
    setArchiving(true);
    try {
      await archivePayAdviceTemplate(businessRecordId, archiveTarget.payAdviceTemplateId, {});
      setArchiveTarget(null);
      setRefresh((value) => value + 1);
      addToast('Template archived.', 'positive');
    } catch (reason) {
      addToast(reason instanceof Error ? reason.message : 'Failed to archive the template.', 'critical');
    } finally {
      setArchiving(false);
    }
  };

  const repoint = async (templateId: string) => {
    try {
      await setDefaultPayAdviceTemplate(businessRecordId, { payAdviceTemplateId: templateId });
      setRefresh((value) => value + 1);
      addToast('Default template updated.', 'positive');
    } catch (reason) {
      addToast(reason instanceof Error ? reason.message : 'Failed to set the default template.', 'critical');
    }
  };

  if (loading && list.length === 0) {
    return <div className="mgo-cluster" role="status" aria-live="polite"><Spinner /><span>Loading Pay Advice Templates…</span></div>;
  }

  if (error) {
    return (
      <Callout tone="critical" title="Pay Advice Templates could not be loaded" action={<Button label="Retry" size="small" variant="secondary" onClick={() => setRefresh((value) => value + 1)} />}>
        {error}
      </Callout>
    );
  }

  const rows = list.map((row) => ({
    id: row.payAdviceTemplateId,
    template: row,
    templateCell: `${row.name}${row.isDefault ? ' · Default' : ''}`,
    subline: `${FDVL_LABEL[row.fdvlDisplayMode] ?? row.fdvlDisplayMode} · ${LEAVE_LABEL[row.leaveProjectionMode] ?? row.leaveProjectionMode}`,
    delivery: `${DELIVERY_LABEL[row.deliveryMethod] ?? row.deliveryMethod}${row.emailEnabled ? ' · email' : ''}${row.portalEnabled ? ' · portal' : ''}`,
    logo: row.hasLogo ? 'Custom' : 'Organisation default',
    backPay: BACK_PAY_LABEL[row.backPayTreatment] ?? row.backPayTreatment,
    aggregation: AGGREGATION_LABEL[row.payTypeAggregation] ?? row.payTypeAggregation,
    status: row.status,
  }));

  // Actions render inside the table's own "Actions" column (via the PrimeReact
  // `body` render prop that Mango's Table forwards) rather than a second list
  // below it — a separate list previously repeated each template's name,
  // which broke single-name locators (Playwright strict mode, screen readers).
  const columns = [
    {
      field: 'templateCell',
      header: 'Template',
      // AC-001's subline (FDVL mode + leave projection) sits under the name;
      // an archived template's name is muted rather than styled identically
      // to an active one, per plan section 10.2.
      body: (row: (typeof rows)[number]) => (
        <div className="mgo-stack mgo-gap-xs">
          <span className={row.template.status === 'archived' ? 'mgo-text-muted' : undefined}>{row.templateCell}</span>
          <span className="mgo-text-s mgo-text-muted">{row.subline}</span>
        </div>
      ),
    },
    { field: 'delivery', header: 'Delivery' },
    { field: 'logo', header: 'Logo' },
    { field: 'backPay', header: 'Back-pay' },
    { field: 'aggregation', header: 'Aggregation' },
    {
      field: 'status',
      header: 'Status',
      body: (row: (typeof rows)[number]) => (
        <Tag label={row.status === 'active' ? 'Active' : 'Archived'} tone={row.status === 'active' ? 'positive' : 'grey'} />
      ),
    },
    {
      field: 'actions',
      header: 'Actions',
      body: (row: (typeof rows)[number]) => (
        <div
          className="mgo-cluster mgo-gap-s"
          onClick={(event) => event.stopPropagation()}
        >
          {row.template.status === 'active' && !row.template.isDefault && (
            <Button
              label="Set as default"
              variant="tertiary"
              size="small"
              disabled={!row.template.actions.canRepointDefault}
              onClick={() => void repoint(row.id)}
            />
          )}
          <Button
            label="Archive"
            variant="tertiary"
            size="small"
            disabled={!row.template.actions.canArchive}
            onClick={() => setArchiveTarget(row.template)}
          />
          {row.template.isDefault && row.template.actions.archiveBlockedReason && (
            <Tag label={row.template.actions.archiveBlockedReason} tone="grey" />
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="mgo-stack mgo-gap-l">
      <div className="mgo-split mgo-align-center">
        <span>
          Pay advice templates <span className="mgo-text-muted">{meta?.activeCount ?? 0} active of {meta?.totalCount ?? 0} total</span>
        </span>
        <Button label="New template" icon="add" variant="primary" disabled={!meta?.canCreate} onClick={openCreate} />
      </div>

      {meta?.businessRecordInactive && (
        <Callout tone="neutral" title="This Business Record is inactive">All Pay Advice configuration on this page is read-only.</Callout>
      )}

      {advisories.map((advisory) => (
        <Callout key={`${advisory.code}-${advisory.targetId ?? ''}`} tone={advisoryTone(advisory.code)}>{advisory.message}</Callout>
      ))}

      <Card>
        {rows.length === 0 ? (
          <EmptyState
            title="No Pay Advice Templates yet"
            description="Every Business Record should carry a default template. If this Business Record has none, its provisioning may not have completed — contact support if this persists."
            actions={meta?.canCreate ? [<Button key="add" label="New template" size="small" onClick={openCreate} />] : []}
          />
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <Table
              value={rows}
              columns={columns}
              stripedRows
              testId="pay-advice-templates-table"
              onRowClick={(row: (typeof rows)[number]) => void openEdit(row.id)}
            />
          </div>
        )}
      </Card>

      <PayAdviceTemplateWizard
        visible={wizardMode !== null}
        mode={wizardMode ?? 'create'}
        template={wizardTemplate}
        mandatoryContent={mandatoryContent}
        readOnly={wizardReadOnly}
        saving={saving}
        saveError={saveError}
        onSave={(request) => void save(request)}
        onClose={() => setWizardMode(null)}
      />

      {/* D-18: archiving is significant but recoverable (destructive-actions.md
          names it as the canonical example), so it confirms through the
          warning severity — a primary confirm, not a destructive one. */}
      <AlertDialog
        visible={archiveTarget !== null}
        severity="warning"
        header="Archive template?"
        confirmLabel="Archive template"
        confirmLoading={archiving}
        onConfirm={() => void confirmArchive()}
        onCancel={() => setArchiveTarget(null)}
      >
        Archive <strong>{archiveTarget?.name}</strong>? It remains queryable for historical Pay Advices but can no
        longer be selected for new configuration.
      </AlertDialog>

      <ToastContainer toasts={toasts} onClose={removeToast} />
    </div>
  );
};
